import { AlbumResponse } from '@klubovna/api-client';
import {
  ALBUM_DEFAULT_IMAGE_PATH,
  ALBUM_DESCRIPTION_FALLBACK,
  ALBUM_DESCRIPTION_MAX_LENGTH,
  ALBUM_SITE_TITLE,
} from '@klubovna/shared-models';
import { PageMeta } from '@klubovna/shared-models';
import { stripHtml } from './event-meta.util';

export interface AlbumMetaOptions {
  lang: 'sk' | 'en';
  baseUrl: string;
  staticContentUrl: string;
  defaultImageUrl?: string;
  fallbackDescription?: string;
  descriptionMaxLength?: number;
}

export function buildAlbumPageMeta(album: AlbumResponse, options: AlbumMetaOptions): PageMeta {
  const fallbackDescription = options.fallbackDescription ?? ALBUM_DESCRIPTION_FALLBACK;
  const descriptionMaxLength = options.descriptionMaxLength ?? ALBUM_DESCRIPTION_MAX_LENGTH;
  const defaultImageUrl = options.defaultImageUrl ?? buildDefaultImageUrl(options.baseUrl);

  const title = getAlbumTitle(album, options.lang);
  const photoCount = album.photos?.length ?? 0;
  const rawDescription = getAlbumDescription(album, options.lang, photoCount, fallbackDescription);
  const sanitizedDescription = stripHtml(rawDescription) || fallbackDescription;
  const description = truncateDescription(sanitizedDescription, descriptionMaxLength);
  const ogImage = resolveImageUrl(album.titlePhoto?.path ?? album.photos?.[0]?.path, {
    defaultImageUrl,
    baseUrl: options.baseUrl,
    staticContentUrl: options.staticContentUrl,
  });
  const metaTags = buildMetaTags({
    description,
    title,
    image: ogImage,
    photoCount,
  });

  const slug = album.slug;
  const urlPath = slug ? `/gallery/${ slug }` : `/gallery/id/${ album.id }`;

  return {
    title: `${ title } - Galéria | ${ ALBUM_SITE_TITLE }`,
    description,
    keywords: `galéria, fotky, ${ title }, spomienky, eventy, (k)lubovňa, Stará Ľubovňa`,
    ogTitle: photoCount > 0 ? `${ title } · ${ photoCount } fotiek` : title,
    ogDescription: description,
    ogUrl: `${ trimTrailingSlash(options.baseUrl) }${ urlPath }`,
    ogImage: ogImage,
    ogType: 'website',
    metaTags,
  };
}

function getAlbumTitle(album: AlbumResponse, lang: 'sk' | 'en'): string {
  return album.name[lang] || album.name['sk'] || album.name['en'] || ALBUM_DESCRIPTION_FALLBACK;
}

function getAlbumDescription(
  album: AlbumResponse,
  lang: 'sk' | 'en',
  photoCount: number,
  fallback: string,
): string {
  const albumDesc = album.description[lang] || album.description['sk'] || album.description['en'];
  if (albumDesc) {
    return albumDesc;
  }

  const albumName = getAlbumTitle(album, lang);
  return photoCount > 0
    ? `Pozri si ${ photoCount } fotiek z albumu ${ albumName }. Galéria spomienok z eventov a aktivít v (k)lubovni SL.`
    : fallback;
}

function truncateDescription(text: string, maxLength: number): string {
  if (!text) {
    return ALBUM_DESCRIPTION_FALLBACK;
  }

  return text.length > maxLength ? `${ text.slice(0, maxLength - 1) }…` : text;
}

function resolveImageUrl(
  imageUrl: string | null | undefined,
  options: { defaultImageUrl: string; baseUrl: string; staticContentUrl: string },
): string {
  const source = imageUrl?.trim();
  if (!source) {
    return options.defaultImageUrl;
  }

  if (/^https?:\/\//i.test(source)) {
    return source;
  }

  if (source.startsWith('//')) {
    return `https:${ source }`;
  }

  if (source.startsWith('/')) {
    return `${ trimTrailingSlash(options.baseUrl) }${ source }`;
  }

  const sanitized = source.replace(/^\/+/, '');
  const staticBase = trimTrailingSlash(options.staticContentUrl);
  return `${ staticBase }/${ sanitized }`;
}

function buildMetaTags({
                         description,
                         title,
                         image,
                         photoCount,
                       }: {
  description: string;
  title: string;
  image: string;
  photoCount: number;
}): Record<string, string> {
  const tags: Record<string, string> = {
    'twitter:card': 'summary_large_image',
    'twitter:title': title,
    'twitter:description': description,
    'twitter:image': image,
  };

  if (photoCount > 0) {
    tags['album:photo_count'] = String(photoCount);
  }

  return tags;
}

function buildDefaultImageUrl(baseUrl: string): string {
  return `${ trimTrailingSlash(baseUrl) }${ ALBUM_DEFAULT_IMAGE_PATH }`;
}

function trimTrailingSlash(value: string): string {
  return value.endsWith('/') ? value.slice(0, -1) : value;
}
